import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { helperGetPatientConsents } from "@/data/platformData";
import {
  Shield, ArrowLeft, Save, FileLock2, Mail, Smartphone, Bell, Microscope,
  UserCircle2, Globe2
} from "lucide-react";

const CURRENT_PATIENT_ID = "1";

export const PatientConsentsPage = () => {
  const navigate = useNavigate();
  const initial = helperGetPatientConsents(CURRENT_PATIENT_ID);
  const [c, setC] = useState(initial);

  const setField = (group, key, value) => {
    setC(prev => ({ ...prev, [group]: { ...prev[group], [key]: value, updatedAt: new Date().toISOString().slice(0, 10) } }));
  };

  const save = () => {
    toast.success("Consensi aggiornati", { description: "Le tue preferenze sono state salvate." });
  };

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto space-y-6">
      <div>
        <Button variant="ghost" size="sm" className="mb-2 -ml-2" onClick={() => navigate("/patient")}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Home
        </Button>
        <div className="flex items-center gap-2 mb-1">
          <Shield className="h-4 w-4 text-emerald-600" />
          <span className="text-xs uppercase tracking-wider text-muted-foreground">Privacy & Consensi</span>
        </div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Le mie preferenze</h1>
        <p className="text-sm text-muted-foreground mt-1">Gestisci come usiamo i tuoi dati sanitari</p>
      </div>

      <Tabs defaultValue="fse" className="space-y-4">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="fse" data-testid="consents-tab-fse">FSE</TabsTrigger>
          <TabsTrigger value="marketing" data-testid="consents-tab-marketing">Comunicazioni</TabsTrigger>
          <TabsTrigger value="caregiver" data-testid="consents-tab-caregiver">Caregiver</TabsTrigger>
          <TabsTrigger value="research" data-testid="consents-tab-research">Ricerca</TabsTrigger>
        </TabsList>

        {/* FSE */}
        <TabsContent value="fse">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <FileLock2 className="h-4 w-4 text-emerald-600" /> Fascicolo Sanitario Elettronico
              </CardTitle>
              <p className="text-xs text-muted-foreground">I tuoi referti possono essere automaticamente sincronizzati con il FSE regionale.</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start justify-between gap-3 p-3 rounded-lg border bg-emerald-50/30">
                <div>
                  <p className="font-medium text-sm">Sincronizzazione automatica con FSE</p>
                  <p className="text-xs text-muted-foreground mt-1">Aggiornato il {c.fse.updatedAt}</p>
                </div>
                <Switch checked={c.fse.active} onCheckedChange={(v) => setField("fse", "active", v)} data-testid="consents-fse-toggle" />
              </div>
              <div className="space-y-2">
                <Label>Ambito condivisione</Label>
                <div className="grid grid-cols-3 gap-2">
                  {[{ id: "full", label: "Completo" }, { id: "summary", label: "Riassunto" }, { id: "minimal", label: "Solo dati base" }].map(opt => (
                    <Button key={opt.id} variant={c.fse.scope === opt.id ? "default" : "outline"} size="sm" disabled={!c.fse.active} onClick={() => setField("fse", "scope", opt.id)}>
                      {opt.label}
                    </Button>
                  ))}
                </div>
              </div>
              <div className="flex items-start justify-between gap-3 p-3 rounded-lg border">
                <div>
                  <p className="font-medium text-sm flex items-center gap-2"><Globe2 className="h-4 w-4 text-sky-600" /> Condivisione regionale</p>
                  <p className="text-xs text-muted-foreground mt-1">Visibile alle strutture sanitarie della tua regione</p>
                </div>
                <Switch checked={c.dataSharing.regional} onCheckedChange={(v) => setField("dataSharing", "regional", v)} />
              </div>
              <div className="flex items-start justify-between gap-3 p-3 rounded-lg border">
                <div>
                  <p className="font-medium text-sm flex items-center gap-2"><Globe2 className="h-4 w-4 text-indigo-600" /> Spazio sanitario europeo (EHDS)</p>
                  <p className="text-xs text-muted-foreground mt-1">Disponibile in caso di urgenze in altri Stati UE</p>
                </div>
                <Switch checked={c.dataSharing.european} onCheckedChange={(v) => setField("dataSharing", "european", v)} />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Marketing */}
        <TabsContent value="marketing">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Bell className="h-4 w-4 text-amber-600" /> Comunicazioni & Marketing
              </CardTitle>
              <p className="text-xs text-muted-foreground">Scegli come vuoi ricevere informazioni su campagne, promemoria e offerte.</p>
            </CardHeader>
            <CardContent className="space-y-3">
              {[
                { key: "email", label: "Email", icon: Mail },
                { key: "sms", label: "SMS", icon: Smartphone },
                { key: "push", label: "Notifiche push", icon: Bell }
              ].map(({ key, label, icon: Icon }) => (
                <div key={key} className="flex items-center justify-between p-3 rounded-lg border bg-card">
                  <div className="flex items-center gap-3">
                    <Icon className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">{label}</span>
                  </div>
                  <Switch checked={c.marketing[key]} onCheckedChange={(v) => setField("marketing", key, v)} data-testid={`consents-marketing-${key}`} />
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Caregiver */}
        <TabsContent value="caregiver">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <UserCircle2 className="h-4 w-4 text-fuchsia-600" /> Caregiver / Tutore
              </CardTitle>
              <p className="text-xs text-muted-foreground">Designa una persona di fiducia che potrà accedere ai tuoi dati e ricevere notifiche.</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start justify-between gap-3 p-3 rounded-lg border bg-fuchsia-50/30">
                <div>
                  <p className="font-medium text-sm">Caregiver attivo</p>
                  <p className="text-xs text-muted-foreground mt-1">Abilita la persona di fiducia</p>
                </div>
                <Switch checked={c.caregiver.enabled} onCheckedChange={(v) => setField("caregiver", "enabled", v)} data-testid="consents-caregiver-toggle" />
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <Label>Nome e cognome</Label>
                  <Input value={c.caregiver.name} disabled={!c.caregiver.enabled} onChange={(e) => setField("caregiver", "name", e.target.value)} />
                </div>
                <div>
                  <Label>Relazione</Label>
                  <Input value={c.caregiver.relationship} disabled={!c.caregiver.enabled} onChange={(e) => setField("caregiver", "relationship", e.target.value)} />
                </div>
                <div>
                  <Label>Telefono</Label>
                  <Input value={c.caregiver.phone} disabled={!c.caregiver.enabled} onChange={(e) => setField("caregiver", "phone", e.target.value)} />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={c.caregiver.email} disabled={!c.caregiver.enabled} onChange={(e) => setField("caregiver", "email", e.target.value)} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Permessi</Label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { id: "view_only", label: "Solo lettura" },
                    { id: "manage_appointments", label: "Gestire prenotazioni" },
                    { id: "full", label: "Accesso completo" }
                  ].map(opt => (
                    <Button key={opt.id} variant={c.caregiver.scope === opt.id ? "default" : "outline"} size="sm" disabled={!c.caregiver.enabled} onClick={() => setField("caregiver", "scope", opt.id)}>
                      {opt.label}
                    </Button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Research */}
        <TabsContent value="research">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Microscope className="h-4 w-4 text-indigo-600" /> Ricerca scientifica
              </CardTitle>
              <p className="text-xs text-muted-foreground">I tuoi dati anonimizzati possono contribuire a studi su salute pubblica e cure innovative.</p>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-start justify-between gap-3 p-3 rounded-lg border">
                <div>
                  <p className="font-medium text-sm">Partecipa alla ricerca</p>
                  <p className="text-xs text-muted-foreground mt-1">Aggiornato il {c.research.updatedAt}</p>
                </div>
                <Switch checked={c.research.active} onCheckedChange={(v) => setField("research", "active", v)} data-testid="consents-research-toggle" />
              </div>
              <div className="flex items-start justify-between gap-3 p-3 rounded-lg border">
                <div>
                  <p className="font-medium text-sm">Solo dati anonimizzati</p>
                  <p className="text-xs text-muted-foreground mt-1">I tuoi dati personali non saranno mai condivisi</p>
                </div>
                <Switch checked={c.research.anonymized} onCheckedChange={(v) => setField("research", "anonymized", v)} disabled={!c.research.active} />
              </div>
              {c.research.active && (
                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                  Conforme GDPR · Diritto di revoca in qualsiasi momento
                </Badge>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="flex justify-end pt-2">
        <Button variant="premium" onClick={save} className="gap-2" data-testid="consents-save-btn">
          <Save className="h-4 w-4" /> Salva preferenze
        </Button>
      </div>
    </div>
  );
};

export default PatientConsentsPage;
